// Module 19 (BL-380) G5 - check-in reads and writes. Every write is one checked database function (migration 246): the caller's own
// organization, the Events pack, Events edit at the event's brand (MFA rules included), the Check-In switches. The tables themselves
// cannot be written from the browser. Nothing here sends anything to a guest.

// True when the G5 functions or tables do not exist yet (code live minutes before migration 246 is applied): check-in then stays hidden.
export function missingCheckinObjects(error) {
    const text = `${error?.code || ''} ${error?.message || ''}`;
    return /PGRST202|PGRST205|42883|42P01/.test(text)
        || (/m19_checkin|m19_event_attendance|m19_attendance_report|event_guest_checkins/.test(text) && /does not exist|schema cache|Could not find/.test(text));
}

async function call(client, name, args) {
    const { data, error } = await client.rpc(name, args);
    if (error) throw error;
    return data;
}

// { guest_id -> { checked_in_at, method } } for one event, or null when check-in is not there yet.
export async function loadCheckins(client, engagementId) {
    const { data, error } = await client.from('event_guest_checkins').select('guest_id, checked_in_at, method').eq('engagement_id', engagementId);
    if (error) {
        if (missingCheckinObjects(error)) return null;
        throw error;
    }
    return new Map((data || []).map((row) => [row.guest_id, row]));
}

// One event's numbers (heads, expected, checked in, walk-ins, no-shows once over), or null when not available.
export async function loadAttendance(client, engagementId) {
    try {
        const rows = await call(client, 'm19_event_attendance', { p_event_id: engagementId });
        return Array.isArray(rows) && rows[0] ? rows[0] : null;
    } catch (error) {
        if (missingCheckinObjects(error)) return null;
        throw error;
    }
}

// Every event's numbers in ONE read, for the board: Map engagement_id -> numbers. Null when not available (the board then shows none).
export async function loadAttendanceMap(client) {
    try {
        const rows = await call(client, 'm19_event_attendance', {});
        return new Map((rows || []).map((row) => [row.engagement_id, row]));
    } catch {
        return null;
    }
}

export const scanPass = (client, engagementId, token, withPlusOnes = false) =>
    call(client, 'm19_checkin_scan', { p_event_id: engagementId, p_token: String(token || '').trim(), p_with_plus_ones: withPlusOnes });
export const checkInGuest = (client, engagementId, guestId, withPlusOnes = false) =>
    call(client, 'm19_checkin_guest', { p_event_id: engagementId, p_guest_id: guestId, p_with_plus_ones: withPlusOnes });
export const undoCheckIn = (client, engagementId, guestId) => call(client, 'm19_checkin_undo', { p_event_id: engagementId, p_guest_id: guestId });
export const loadPasses = (client, engagementId, guestId = null) =>
    call(client, 'm19_checkin_passes', guestId ? { p_event_id: engagementId, p_guest_id: guestId } : { p_event_id: engagementId });
export const replacePass = (client, engagementId, guestId) => call(client, 'm19_checkin_replace_pass', { p_event_id: engagementId, p_guest_id: guestId });
export const loadAttendanceReport = (client, engagementId) => call(client, 'm19_attendance_report', { p_event_id: engagementId });

// Database wording -> plain English. The functions' own messages are already plain; anything else gets a safe line.
export function friendlyCheckinError(error) {
    const text = `${error?.message || ''}`;
    if (missingCheckinObjects(error)) return 'Check-in is not available yet. Please try again in a few minutes.';
    if (/row-level security|permission denied/.test(text)) return 'You do not have permission to check guests in.';
    if (/Failed to fetch|NetworkError|network/i.test(text)) return 'No connection. Check the network and try again.';
    if (['22023', '42501', 'P0002'].includes(error?.code)) return text;
    return text || 'That did not work. Please try again.';
}
